import { useState, useCallback, useEffect, useRef } from "react";
import {
  useLocalStorage,
  useLocalStorageKey,
  getLocalStorageByKey,
} from "@adsk/acc-issues-core/utils/hooks/useLocalStorage";
import { LocalStorageKeys } from "@adsk/acc-issues-core/state/localStorage";

export const usePersistedState = <T>(
  storageKey: LocalStorageKeys,
  suffix: string,
  defaultValue: T
): [T, (newValue: T | ((prev: T) => T)) => void] => {
  const [getStoredValue, setStoredValue] = useLocalStorage(storageKey);
  const localStorageKey = useLocalStorageKey(storageKey);
  const isInitialMount = useRef(true);

  const [state, setState] = useState<T>(() => {
    try {
      const allStoredData = getStoredValue();
      return allStoredData && allStoredData[suffix] !== undefined
        ? allStoredData[suffix]
        : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    // Skip saving on initial mount to avoid overwriting just-loaded data
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    try {
      const currentStoredData = getLocalStorageByKey(localStorageKey) || {};
      const updatedData = {
        ...currentStoredData,
        [suffix]: state,
      };

      setStoredValue(updatedData);
    } catch {
      // Catch localStorage errors to prevent crashes
      // Table will still function, just without persisted preferences
    }
  }, [state, suffix, localStorageKey, setStoredValue]);

  const setValue = useCallback((newValue: T | ((prev: T) => T)) => {
    setState(prevState =>
      typeof newValue === "function"
        ? (newValue as (prev: T) => T)(prevState)
        : newValue
    );
  }, []);

  return [state, setValue];
};

export const useClearTableState = () => {
  const [, setStoredValue] = useLocalStorage(
    LocalStorageKeys.ISSUE_LOG_COLUMN_SETTINGS
  );

  return useCallback(() => {
    try {
      setStoredValue({});
    } catch {
      // Catch localStorage errors to prevent crashes - reset will still work in memory
    }
  }, [setStoredValue]);
};
